import React from 'react';
import useLang from '../hooks/useLang';

const AGBDatenschutz = ({ onClose }) => {
  const { lang } = useLang();
  const urlParams = new URLSearchParams(window.location.search);
  const isFreeMode = (process.env.REACT_APP_FREE_MODE === 'true') || (urlParams.get('free') === 'true');
  
  const content = {
    de: {
      title: "AGB & Datenschutz",
      subtitle: "Allgemeine Geschäftsbedingungen (AGB) und Datenschutzhinweise für Centeo",
      date: "Stand: 15. Juli 2025",
      preamble: "Präambel",
      preambleText: "Diese Allgemeinen Geschäftsbedingungen (AGB) regeln die Nutzung des Online-Dienstes \"Centeo\", betrieben durch die Lazzaro One UG (haftungsbeschränkt), Forckenbeckstraße 63c, 14199 Berlin (nachfolgend \"wir\" oder \"Centeo\").",
      section1: "§ 1 Leistungsgegenstand",
      section2: "§ 2 Vertragsschluss und Zahlung", 
      section3: "§ 3 Widerrufsrecht für digitale Inhalte",
      section4: "§ 4 Haftungsausschluss",
      section5: "§ 5 Geld-zurück-Garantie",
      privacyTitle: "Datenschutzerklärung",
      closeButton: "Schließen"
    },
    es: {
      title: "Términos y Privacidad",
      subtitle: "Términos y Condiciones Generales y Aviso de Privacidad para Centeo",
      date: "Fecha: 15 de julio de 2025",
      preamble: "Preámbulo",
      preambleText: "Estos Términos y Condiciones Generales regulan el uso del servicio en línea \"Centeo\", operado por Lazzaro One UG (haftungsbeschränkt), Forckenbeckstraße 63c, 14199 Berlin (en adelante \"nosotros\" o \"Centeo\").",
      section1: "§ 1 Objeto del servicio",
      section2: "§ 2 Conclusión del contrato y pago",
      section3: "§ 3 Derecho de desistimiento para contenido digital",
      section4: "§ 4 Exclusión de responsabilidad",
      section5: "§ 5 Garantía de devolución del dinero",
      privacyTitle: "Declaración de Privacidad",
      closeButton: "Cerrar"
    },
    it: {
      title: "Termini e Privacy",
      subtitle: "Termini e Condizioni Generali e Informativa sulla Privacy per Centeo",
      date: "Data: 15 luglio 2025",
      preamble: "Preambolo",
      preambleText: "Questi Termini e Condizioni Generali regolano l'uso del servizio online \"Centeo\", gestito da Lazzaro One UG (haftungsbeschränkt), Forckenbeckstraße 63c, 14199 Berlin (di seguito \"noi\" o \"Centeo\").",
      section1: "§ 1 Oggetto del servizio",
      section2: "§ 2 Conclusione del contratto e pagamento",
      section3: "§ 3 Diritto di recesso per contenuti digitali",
      section4: "§ 4 Esclusione di responsabilità",
      section5: "§ 5 Garanzia di rimborso",
      privacyTitle: "Dichiarazione sulla Privacy",
      closeButton: "Chiudi"
    }
  };

  const t = content[lang] || content.de;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{t.title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>
        
        <div className="prose max-w-none text-sm">
          <h3 className="text-lg font-semibold mb-3">{t.subtitle}</h3>
          <p className="text-gray-600 mb-6">{t.date}</p>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">{t.preamble}</h4>
            <p>{t.preambleText}</p>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">{t.section1}</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Centeo bietet Nutzern (nachfolgend "Kunde") eine einmalige, automatisierte Analyse zur 
                Identifizierung potenziell relevanter Förderprogramme für landwirtschaftliche Betriebe 
                auf Basis der vom Kunden eingegebenen Daten.
              </li>
              {isFreeMode ? (
                <li>
                  Das Ergebnis der Analyse wird dem Kunden nach Eingabe der E-Mail und Zustimmung zu AGB & Datenschutz unmittelbar auf der Webseite angezeigt. Eine Zahlung ist derzeit nicht erforderlich.
                </li>
              ) : (
                <li>
                  Das Ergebnis der Analyse wird dem Kunden nach erfolgreicher Zahlung unmittelbar auf der Webseite angezeigt.
                </li>
              )}
              <li>
                Die von Centeo bereitgestellten Informationen stellen ausdrücklich keine Förderberatung, 
                Finanzberatung oder Rechtsberatung dar.
              </li>
            </ul>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">{t.section2}</h4>
            {isFreeMode ? (
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Vorübergehende kostenlose Nutzung: Der Vertrag kommt zustande, indem der Kunde die erforderlichen Daten eingibt und AGB sowie Datenschutzhinweise akzeptiert. Eine Zahlung ist nicht erforderlich.
                </li>
                <li>
                  Es werden keine Zahlungsdaten erhoben; Drittanbieter-Zahlungsabwicklungen (z. B. Stripe) sind während der kostenlosen Phase deaktiviert.
                </li>
              </ul>
            ) : (
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Der Vertrag über die Nutzung der Analyse kommt zustande, indem der Kunde die erforderlichen Daten eingibt, diese AGB und die Datenschutzhinweise akzeptiert und den Zahlungsvorgang erfolgreich abschließt.
                </li>
                <li>
                  Die Vergütung für die einmalige Analyse beträgt 9,90 € inkl. der gesetzlichen Mehrwertsteuer.
                </li>
                <li>
                  Die Zahlungsabwicklung erfolgt über den externen Dienstleister Stripe, Inc. Es gelten dessen Nutzungs- und Datenschutzbedingungen.
                </li>
              </ul>
            )}
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">{t.section3}</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>Verbrauchern steht grundsätzlich ein gesetzliches Widerrufsrecht zu.</li>
              <li>
                <strong>Verzicht auf das Widerrufsrecht:</strong> Da es sich bei der Analyse von Centeo um eine 
                unmittelbar auf Kundenanforderung erstellte und bereitgestellte digitale Dienstleistung handelt, 
                stimmt der Kunde vor Abschluss der Bestellung ausdrücklich zu, dass Centeo mit der Ausführung des 
                Vertrags vor Ablauf der Widerrufsfrist beginnt.
              </li>
              <li>
                Der Kunde nimmt zur Kenntnis und bestätigt aktiv per Checkbox, dass sein Widerrufsrecht mit 
                vollständiger Erbringung der Dienstleistung (d.h. mit Anzeige der Analyse-Ergebnisse) erlischt.
              </li>
            </ul>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">{t.section4}</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Centeo übernimmt keine Gewähr für die Aktualität, Richtigkeit, Vollständigkeit oder fortwährende 
                Gültigkeit der in der Datenbank enthaltenen Förderdaten. Förderprogramme unterliegen ständigen 
                Änderungen durch die zuständigen Behörden.
              </li>
              <li>
                Der von unserem Algorithmus berechnete "Score" ist eine unverbindliche, automatisierte Einschätzung 
                und stellt keine Garantie für eine erfolgreiche Antragsstellung oder den Erhalt von Fördermitteln dar.
              </li>
              <li>
                Die endgültige Prüfung der Förderfähigkeit sowie die form- und fristgerechte Beantragung obliegen 
                allein der Verantwortung des Kunden. Wir empfehlen dringend, vor jeder Antragsstellung die offiziellen 
                Programmunterlagen der jeweiligen Förderstelle zu konsultieren.
              </li>
            </ul>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">{t.section5}</h4>
            {lang === 'de' && (
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Wir bieten für unsere Analyse eine 30-tägige Geld-zurück-Garantie an, beginnend mit dem 
                  Datum des Kaufs.
                </li>
                <li>
                  Sollte der Kunde mit der Qualität der gelieferten Analyse-Ergebnisse begründet unzufrieden 
                  sein, kann er innerhalb von 30 Tagen eine vollständige Rückerstattung des Kaufpreises beantragen.
                </li>
                <li>
                  Die Beantragung erfolgt formlos per E-Mail an office@lazzaro.one unter Angabe der E-Mail-Adresse, 
                  die für den Kauf verwendet wurde.
                </li>
                <li>
                  Die Rückerstattung erfolgt über unseren Zahlungsdienstleister Stripe auf die ursprünglich 
                  verwendete Zahlungsmethode. Die Garantie ist nicht als kostenlose Testversion zu verstehen 
                  und gilt nicht bei missbräuchlicher Inanspruchnahme.
                </li>
              </ul>
            )}
            {lang === 'es' && (
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Ofrecemos una garantía de devolución del dinero de 30 días para nuestro análisis, 
                  comenzando desde la fecha de compra.
                </li>
                <li>
                  Si el cliente está justificadamente insatisfecho con la calidad de los resultados del 
                  análisis entregados, puede solicitar un reembolso completo del precio de compra dentro de 30 días.
                </li>
                <li>
                  La solicitud se realiza de forma informal por correo electrónico a office@lazzaro.one 
                  indicando la dirección de correo electrónico utilizada para la compra.
                </li>
                <li>
                  El reembolso se realiza a través de nuestro proveedor de servicios de pago Stripe al 
                  método de pago utilizado originalmente. La garantía no debe entenderse como una versión 
                  de prueba gratuita y no se aplica en caso de uso abusivo.
                </li>
              </ul>
            )}
            {lang === 'it' && (
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Offriamo una garanzia di rimborso di 30 giorni per la nostra analisi, a partire dalla 
                  data di acquisto.
                </li>
                <li>
                  Se il cliente è giustificatamente insoddisfatto della qualità dei risultati dell'analisi 
                  forniti, può richiedere un rimborso completo del prezzo di acquisto entro 30 giorni.
                </li>
                <li>
                  La richiesta viene effettuata informalmente via e-mail a office@lazzaro.one indicando 
                  l'indirizzo e-mail utilizzato per l'acquisto.
                </li>
                <li>
                  Il rimborso viene effettuato tramite il nostro fornitore di servizi di pagamento Stripe 
                  al metodo di pagamento utilizzato originariamente. La garanzia non deve essere intesa come 
                  una versione di prova gratuita e non si applica in caso di uso improprio.
                </li>
              </ul>
            )}
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">{t.privacyTitle}</h3>
            
            <h4 className="font-semibold mb-2">1. Verantwortliche Stelle</h4>
            <p className="mb-4">
              Verantwortlich für die Datenverarbeitung auf dieser Webseite ist die Lazzaro One UG (haftungsbeschränkt). 
              Die Kontaktdaten finden Sie oben im Impressum.
            </p>

            <h4 className="font-semibold mb-2">2. Erhebung und Verarbeitung von Daten</h4>
            <p className="mb-2">Wir erheben und verarbeiten personenbezogene Daten zu folgenden Zwecken:</p>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>
                <strong>Zur Durchführung der Analyse:</strong> Wir verarbeiten die vom Kunden in das Formular 
                eingegebenen Betriebsdaten (Land, Region, Betriebsart, Größe, Interessen etc.), um die Analyse 
                durchzuführen und die passenden Förderprogramme zu identifizieren. Rechtsgrundlage ist die Erfüllung 
                des Vertrags gemäß Art. 6 Abs. 1 lit. b DSGVO. Diese Daten werden nach Abschluss der Analyse 
                anonymisiert für statistische Zwecke weiterverwendet.
              </li>
              {!isFreeMode && (
                <li>
                  <strong>Zur Zahlungsabwicklung:</strong> Für die Abwicklung der Zahlung erheben wir Ihren Namen und 
                  Ihre E-Mail-Adresse. Die eigentlichen Zahlungsdaten (Kreditkartennummer etc.) werden nicht von uns, 
                  sondern direkt von unserem Zahlungsdienstleister Stripe Payments Europe, Ltd. verarbeitet. Wir erhalten 
                  von Stripe lediglich eine Bestätigung über die erfolgte Zahlung. Rechtsgrundlage ist die Vertragserfüllung 
                  gemäß Art. 6 Abs. 1 lit. b DSGVO.
                </li>
              )}
              <li>
                <strong>Server-Log-Dateien:</strong> Unser Webserver speichert bei jedem Zugriff automatisch Informationen 
                in Log-Dateien (z.B. IP-Adresse, Uhrzeit, Browsertyp). Diese Daten dienen der Sicherstellung eines 
                störungsfreien Betriebs und der Sicherheit unserer Systeme. Rechtsgrundlage ist unser berechtigtes 
                Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO.
              </li>
            </ul>

            <h4 className="font-semibold mb-2">3. Datenspeicherung und -sicherheit</h4>
            <p className="mb-4">
              Wir speichern personenbezogene Daten nur so lange, wie es für die Erfüllung der genannten Zwecke oder 
              aufgrund gesetzlicher Aufbewahrungsfristen (z.B. aus dem Steuerrecht) erforderlich ist.
            </p>

            <h4 className="font-semibold mb-2">4. Cookies</h4>
            <p className="mb-4">
              Unsere Webseite verwendet technisch notwendige Cookies, um die Funktion des Dienstes (z.B. das Speichern 
              der Nutzersitzung) zu gewährleisten. Wir verwenden keine Tracking- oder Marketing-Cookies von Drittanbietern 
              ohne Ihre ausdrückliche Einwilligung, die wir über einen Cookie-Banner einholen.
            </p>

            <h4 className="font-semibold mb-2">5. Ihre Rechte als Betroffener</h4>
            <p className="mb-4">
              Sie haben jederzeit das Recht auf unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten, 
              deren Herkunft und Empfänger und den Zweck der Datenverarbeitung sowie ein Recht auf Berichtigung, Sperrung 
              oder Löschung dieser Daten. Hierzu sowie zu weiteren Fragen zum Thema personenbezogene Daten können Sie sich 
              jederzeit unter der im Impressum angegebenen Adresse an uns wenden. Ihnen steht zudem ein Beschwerderecht bei 
              der zuständigen Aufsichtsbehörde zu.
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t.closeButton}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AGBDatenschutz;
